// OnboardingGate - Shown to authenticated users who have no clientId (organization) assigned.
// Two modes: invite redemption (if URL has /invite/:token) or join request form.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { Toaster } from '@/components/ui/toaster';
import { toast } from '@/hooks/use-toast';
import {
  Building2,
  LogOut,
  Send,
  Clock,
  XCircle,
  CheckCircle2,
  Shield,
  Loader2,
} from 'lucide-react';

interface OnboardingGateProps {
  user: any;
  onLogout: () => void;
}

export function OnboardingGate({ user, onLogout }: OnboardingGateProps) {
  // Check if URL is an invite link
  const inviteToken = extractInviteToken(window.location.pathname);

  if (inviteToken) {
    return <InviteRedemption token={inviteToken} user={user} onLogout={onLogout} />;
  }

  return <JoinRequestForm user={user} onLogout={onLogout} />;
}

function extractInviteToken(pathname: string): string | null {
  const match = pathname.match(/^\/invite\/([^/]+)$/);
  return match ? match[1] : null;
}

// =============================================
// Invite Redemption Mode
// =============================================

function InviteRedemption({
  token,
  user,
  onLogout,
}: {
  token: string;
  user: any;
  onLogout: () => void;
}) {
  const queryClient = useQueryClient();

  const {
    data: invite,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['invite-validate', token],
    queryFn: () => api.onboarding.validateInvite(token),
  });

  const redeemMutation = useMutation({
    mutationFn: () => api.onboarding.redeemInvite(token),
    onSuccess: () => {
      toast({ title: 'Welcome!', description: 'You have joined the organization.', variant: 'default' });
      // Invalidate profile so root layout re-fetches and sees the clientId
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      // Navigate to dashboard
      window.location.href = '/';
    },
    onError: () => {
      // Error toast handled by api-client
    },
  });

  return (
    <OnboardingShell user={user} onLogout={onLogout}>
      <div className="glass-elevated rounded-2xl p-8 relative">
        <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-gold-500/50 to-transparent" />

        {isLoading && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="w-8 h-8 text-gold-400 animate-spin" />
            <p className="text-muted-foreground">Validating invite...</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-4 py-8">
            <XCircle className="w-12 h-12 text-red-400" />
            <h3 className="text-lg font-semibold text-foreground">Invalid Invite</h3>
            <p className="text-muted-foreground text-center text-sm">
              {(error as any)?.message || 'This invite link is invalid, expired, or has already been used.'}
            </p>
          </div>
        )}

        {invite?.valid && (
          <div className="flex flex-col items-center gap-6 py-4">
            <div className="w-16 h-16 rounded-2xl bg-gold-500/10 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-gold-400" />
            </div>

            <div className="text-center">
              <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                You've been invited
              </h3>
              <p className="text-muted-foreground text-sm">
                Join <span className="text-foreground font-medium">{invite.clientName}</span> as a{' '}
                <span className="text-gold-400 font-medium">{invite.role}</span>
              </p>
            </div>

            <button
              onClick={() => redeemMutation.mutate()}
              disabled={redeemMutation.isPending}
              className="w-full py-3 px-6 btn-gold rounded-xl flex items-center justify-center gap-2 text-base disabled:opacity-50"
            >
              {redeemMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <CheckCircle2 className="w-5 h-5" />
              )}
              <span>{redeemMutation.isPending ? 'Joining...' : 'Accept & Join'}</span>
            </button>
          </div>
        )}
      </div>
    </OnboardingShell>
  );
}

// =============================================
// Join Request Form Mode
// =============================================

function JoinRequestForm({ user, onLogout }: { user: any; onLogout: () => void }) {
  const [orgSlug, setOrgSlug] = useState('');
  const queryClient = useQueryClient();

  // Fetch existing join requests
  const { data: myRequests, isLoading: requestsLoading } = useQuery({
    queryKey: ['my-join-requests'],
    queryFn: () => api.onboarding.myJoinRequests(),
  });

  const submitMutation = useMutation({
    mutationFn: (slug: string) => api.onboarding.submitJoinRequest(slug),
    onSuccess: () => {
      toast({ title: 'Request Submitted', description: 'An admin will review your request.', variant: 'default' });
      setOrgSlug('');
      queryClient.invalidateQueries({ queryKey: ['my-join-requests'] });
    },
  });

  const hasPending = myRequests?.some((r: any) => r.status === 'pending');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgSlug.trim()) return;
    submitMutation.mutate(orgSlug.trim());
  };

  return (
    <OnboardingShell user={user} onLogout={onLogout}>
      {/* Join Request Form Card */}
      <div className="glass-elevated rounded-2xl p-8 relative">
        <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-gold-500/50 to-transparent" />

        <div className="flex flex-col items-center gap-2 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gold-500/10 flex items-center justify-center mb-2">
            <Building2 className="w-7 h-7 text-gold-400" />
          </div>
          <h2 className="text-xl font-display font-semibold text-foreground">
            Request to Join an Organization
          </h2>
          <p className="text-muted-foreground text-sm text-center max-w-sm">
            Enter your organization's name or identifier. A platform admin will review your request.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-muted-foreground uppercase tracking-widest mb-2 font-medium">
              Organization Name or Identifier
            </label>
            <input
              type="text"
              value={orgSlug}
              onChange={(e) => setOrgSlug(e.target.value)}
              placeholder="e.g. acme-corp or Acme Corporation"
              disabled={hasPending || submitMutation.isPending}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-gold-500/50 focus:border-gold-500/50 transition-all disabled:opacity-50"
            />
          </div>

          <button
            type="submit"
            disabled={!orgSlug.trim() || hasPending || submitMutation.isPending}
            className="w-full py-3 px-6 btn-gold rounded-xl flex items-center justify-center gap-2 text-base disabled:opacity-50"
          >
            {submitMutation.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
            <span>{submitMutation.isPending ? 'Submitting...' : 'Submit Request'}</span>
          </button>
        </form>
      </div>

      {/* Existing Requests */}
      {!requestsLoading && myRequests && myRequests.length > 0 && (
        <div className="glass-elevated rounded-2xl p-6 mt-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-4">
            Your Requests
          </h3>
          <div className="space-y-3">
            {myRequests.map((req: any) => (
              <RequestStatusCard key={req.id} request={req} />
            ))}
          </div>
        </div>
      )}
    </OnboardingShell>
  );
}

// =============================================
// Shared Components
// =============================================

function OnboardingShell({
  user,
  onLogout,
  children,
}: {
  user: any;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen gradient-animated noise overflow-hidden relative">
      {/* Background (same as LoginScreen) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-500/5 rounded-full blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--gold-500)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--gold-500)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="flex flex-col items-center justify-center min-h-screen px-4 relative z-10">
        <div className="w-full max-w-md">
          {/* User Identity Bar */}
          <div className="flex items-center justify-between mb-6 animate-slideUp">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-gold flex items-center justify-center text-primary-950 font-semibold text-sm">
                {user?.firstName?.[0] || user?.email?.[0] || 'U'}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {user?.firstName ? `${user.firstName} ${user.lastName || ''}` : user?.email}
                </p>
                <p className="text-2xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-all text-sm"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign out</span>
            </button>
          </div>

          {/* Content */}
          <div className="animate-slideUp" style={{ animationDelay: '0.1s' }}>
            {children}
          </div>

          {/* Security footer */}
          <div className="flex items-center justify-center gap-2 mt-8 animate-slideUp" style={{ animationDelay: '0.2s' }}>
            <Shield className="w-3 h-3 text-muted-foreground/50" />
            <p className="text-xs text-muted-foreground/50">
              Secured with Microsoft Entra ID
            </p>
          </div>
        </div>
      </div>

      <Toaster />
    </div>
  );
}

function RequestStatusCard({ request }: { request: any }) {
  const statusConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
    pending: {
      icon: <Clock className="w-4 h-4" />,
      color: 'text-amber-400',
      label: 'Pending Review',
    },
    approved: {
      icon: <CheckCircle2 className="w-4 h-4" />,
      color: 'text-green-400',
      label: 'Approved',
    },
    denied: {
      icon: <XCircle className="w-4 h-4" />,
      color: 'text-red-400',
      label: 'Denied',
    },
  };

  const config = statusConfig[request.status] || statusConfig.pending;

  return (
    <div className="flex items-start justify-between p-3 rounded-lg bg-background/50 border border-border">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {request.organizationSlug}
        </p>
        <p className="text-2xs text-muted-foreground mt-0.5">
          {new Date(request.createdAt).toLocaleDateString()}
        </p>
        {request.status === 'denied' && request.denyReason && (
          <p className="text-2xs text-red-400 mt-1">
            Reason: {request.denyReason}
          </p>
        )}
      </div>
      <div className={`flex items-center gap-1.5 ${config.color}`}>
        {config.icon}
        <span className="text-xs font-medium">{config.label}</span>
      </div>
    </div>
  );
}
